import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Receipt, Plus, Trash2, Edit, Banknote, DollarSign, X, ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown, Filter } from "lucide-react";
import { Expense, EXPENSE_CATEGORIES } from "@/types/expense";
import { DeleteDialog } from "@/components/ui/confirmation-dialog";

interface ExpensesPageProps {
  expenses: Expense[];
  onAddExpense: (expense: Omit<Expense, "id" | "createdAt">) => void;
  onUpdateExpense: (expense: Expense) => void;
  onDeleteExpense: (id: string) => void | Promise<void>;
  addDialogOpen: boolean;
  onAddDialogOpenChange: (open: boolean) => void;
}

export function ExpensesPage({ expenses, onAddExpense, onUpdateExpense, onDeleteExpense, addDialogOpen, onAddDialogOpenChange }: ExpensesPageProps) {
  const [startDate, setStartDate] = useState("2026-01-01");
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);
  const [expenseToEdit, setExpenseToEdit] = useState<Expense | null>(null);
  const [expandedExpenses, setExpandedExpenses] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Add Form state
  const [addTitle, setAddTitle] = useState("");
  const [addDescription, setAddDescription] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addCurrency, setAddCurrency] = useState<"IQD" | "USD">("IQD");
  const [addCategory, setAddCategory] = useState<string>(EXPENSE_CATEGORIES[0]);
  const [addDate, setAddDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [addNotes, setAddNotes] = useState("");

  // Edit Form state
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editCurrency, setEditCurrency] = useState<"IQD" | "USD">("IQD");
  const [editCategory, setEditCategory] = useState<string>(EXPENSE_CATEGORIES[0]);
  const [editDate, setEditDate] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const resetAddForm = () => {
    setAddTitle("");
    setAddDescription("");
    setAddAmount("");
    setAddCurrency("IQD");
    setAddCategory(EXPENSE_CATEGORIES[0]);
    setAddDate(new Date().toISOString().split('T')[0]);
    setAddNotes("");
  };

  const clearDates = () => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate("2026-01-01");
    setEndDate(today);
    setSelectedCategory("all");
  };

  const toggleExpanded = (id: string) => {
    setExpandedExpenses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortDirection === "desc") setSortDirection("asc");
      else { setSortField(null); setSortDirection("desc"); }
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    return sortDirection === "asc" ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const expenseDate = expense.date;
      const dateMatch = expenseDate >= startDate && expenseDate <= endDate;
      const categoryMatch = selectedCategory === "all" || expense.category === selectedCategory;
      return dateMatch && categoryMatch;
    });
  }, [expenses, startDate, endDate, selectedCategory]);

  const sortedExpenses = useMemo(() => {
    if (!sortField) return [...filteredExpenses].sort((a, b) => b.date.localeCompare(a.date));
    return [...filteredExpenses].sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (sortField) {
        case "date": aVal = a.date; bVal = b.date; break;
        case "title": aVal = a.title; bVal = b.title; break;
        case "category": aVal = a.category; bVal = b.category; break;
        case "amount": aVal = a.amount; bVal = b.amount; break;
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDirection === "asc" ? String(aVal).localeCompare(String(bVal), "ar") : String(bVal).localeCompare(String(aVal), "ar");
    });
  }, [filteredExpenses, sortField, sortDirection]);

  const totals = useMemo(() => {
    return filteredExpenses.reduce((acc, e) => ({
      count: acc.count + 1,
      totalIQD: acc.totalIQD + (e.currency === "IQD" ? e.amount : 0),
      totalUSD: acc.totalUSD + (e.currency === "USD" ? e.amount : 0),
    }), { count: 0, totalIQD: 0, totalUSD: 0 });
  }, [filteredExpenses]);

  const handleAddExpense = () => {
    const parsedAmount = parseFloat(addAmount);
    if (!addTitle.trim() || isNaN(parsedAmount) || parsedAmount <= 0) return;
    onAddExpense({
      title: addTitle.trim(),
      description: addDescription.trim(),
      amount: parsedAmount,
      currency: addCurrency,
      category: addCategory,
      date: addDate,
      notes: addNotes.trim(),
    });
    resetAddForm();
    onAddDialogOpenChange(false);
  };

  const handleEditExpense = () => {
    const parsedAmount = parseFloat(editAmount);
    if (!expenseToEdit || !editTitle.trim() || isNaN(parsedAmount) || parsedAmount <= 0) return;
    onUpdateExpense({
      ...expenseToEdit,
      title: editTitle.trim(),
      description: editDescription.trim(),
      amount: parsedAmount,
      currency: editCurrency,
      category: editCategory,
      date: editDate,
      notes: editNotes.trim(),
    });
    setExpenseToEdit(null);
    setEditDialogOpen(false);
  };

  const openEditDialog = (expense: Expense) => {
    setExpenseToEdit(expense);
    setEditTitle(expense.title);
    setEditDescription(expense.description);
    setEditAmount(expense.amount.toString());
    setEditCurrency(expense.currency);
    setEditCategory(expense.category);
    setEditDate(expense.date);
    setEditNotes(expense.notes);
    setEditDialogOpen(true);
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('ku', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="space-y-4" dir="rtl">
      {/* Filter Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-5 w-5" />فلتەرکردن
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={clearDates}
              className="flex items-center gap-1.5 text-xs h-8"
            >
              <X className="h-3.5 w-3.5" />
              پاککردنەوە
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">لە بەرواری</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                dir="ltr"
                className="text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">بۆ بەرواری</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                dir="ltr"
                className="text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">جۆر</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">هەموو</SelectItem>
                  {EXPENSE_CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Receipt className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold">{totals.count}</p>
          <p className="text-xs text-muted-foreground">کۆی خەرجییەکان</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Banknote className="h-4 w-4 text-amber-600" />
            </div>
          </div>
          <p className="text-xl font-bold flex items-center gap-1">
            <span dir="ltr">{totals.totalIQD.toLocaleString()}</span>
            <span className="text-xs font-normal text-muted-foreground">د.ع</span>
          </p>
          <p className="text-xs text-muted-foreground">کۆی دینار</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-green-600" />
            </div>
          </div>
          <p className="text-xl font-bold" dir="ltr">${totals.totalUSD.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">کۆی دۆلار</p>
        </div>
      </div>

      {/* Expenses Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />خەرجییەکان
          </CardTitle>
          <CardDescription>{sortedExpenses.length} خەرجی لە ماوەی دیاریکراو</CardDescription>
        </CardHeader>
        <CardContent>
          {sortedExpenses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              هیچ خەرجییەک نییە بۆ ئەم ماوەیە
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="rounded-md border hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "date" ? "bg-muted/30" : ""}`} onClick={() => handleSort("date")}>
                        <div className="flex items-center gap-1">بەروار<SortIcon field="date" /></div>
                      </TableHead>
                      <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "title" ? "bg-muted/30" : ""}`} onClick={() => handleSort("title")}>
                        <div className="flex items-center gap-1">ناو<SortIcon field="title" /></div>
                      </TableHead>
                      <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "category" ? "bg-muted/30" : ""}`} onClick={() => handleSort("category")}>
                        <div className="flex items-center gap-1">جۆر<SortIcon field="category" /></div>
                      </TableHead>
                      <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "amount" ? "bg-muted/30" : ""}`} onClick={() => handleSort("amount")}>
                        <div className="flex items-center gap-1">بڕ<SortIcon field="amount" /></div>
                      </TableHead>
                      <TableHead className="text-right">تێبینی</TableHead>
                      <TableHead className="text-right w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedExpenses.map((expense, index) => (
                      <TableRow key={expense.id} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                        <TableCell dir="ltr" className="text-right">{expense.date}</TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium">{expense.title}</span>
                            {expense.description && <p className="text-sm text-muted-foreground">{expense.description}</p>}
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline">{expense.category}</Badge></TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-1.5 justify-start">
                            <span className="font-medium" dir="ltr">{expense.amount.toLocaleString()}</span>
                            <Badge variant={expense.currency === "IQD" ? "secondary" : "default"} className="text-[10px] px-1 py-0 h-4">
                              {expense.currency === "IQD" ? "د.ع" : "$"}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[120px] truncate text-muted-foreground">{expense.notes || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              className="h-8 w-8 p-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow transition-all hover:-translate-y-0.5 cursor-pointer"
                              onClick={() => openEditDialog(expense)}
                              title="دەستکاری"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="h-8 w-8 p-0 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow transition-all hover:-translate-y-0.5 cursor-pointer"
                              onClick={() => { setExpenseToDelete(expense.id); setDeleteConfirmOpen(true); }}
                              title="سڕینەوە"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {sortedExpenses.map((expense, index) => {
                  const isExpanded = expandedExpenses.has(expense.id);
                  const isEven = index % 2 === 1;
                  return (
                    <div key={expense.id} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                      <div className="p-3 cursor-pointer" onClick={() => toggleExpanded(expense.id)}>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                            <Receipt className="h-5 w-5 text-red-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm truncate">{expense.title}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 h-4">{expense.category}</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">{formatDate(expense.date)}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="font-bold text-sm flex items-center gap-1">
                              {expense.currency === "IQD" ? (
                                <>
                                  <span dir="ltr">{expense.amount.toLocaleString()}</span>
                                  <span className="text-xs text-muted-foreground">د.ع</span>
                                </>
                              ) : (
                                <span dir="ltr">${expense.amount.toLocaleString()}</span>
                              )}
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleExpanded(expense.id); }}>
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                          <div className="space-y-2 pt-3">
                            {expense.description && (
                              <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                <span className="text-muted-foreground text-xs">وەسف</span>
                                <span className="font-medium text-sm">{expense.description}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                              <span className="text-muted-foreground text-xs">جۆر</span>
                              <Badge variant="outline">{expense.category}</Badge>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                              <span className="text-muted-foreground text-xs">بڕ</span>
                              <div className="font-medium text-sm flex items-center gap-1">
                                {expense.currency === "IQD" ? (
                                  <>
                                    <span dir="ltr">{expense.amount.toLocaleString()}</span>
                                    <span className="text-xs">د.ع</span>
                                  </>
                                ) : (
                                  <span dir="ltr">${expense.amount.toLocaleString()}</span>
                                )}
                              </div>
                            </div>
                            {expense.notes && (
                              <div className="pt-2 mt-1 border-t">
                                <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                                <p className="text-sm bg-muted/50 rounded-lg p-2 text-muted-foreground">{expense.notes}</p>
                              </div>
                            )}
                            <div className="flex gap-2 pt-2">
                              <Button size="sm" variant="outline" className="flex-1" onClick={(e) => { e.stopPropagation(); openEditDialog(expense); }}>
                                <Edit className="h-4 w-4 ml-1" />دەستکاری
                              </Button>
                              <Button size="sm" variant="destructive" className="flex-1" onClick={(e) => { e.stopPropagation(); setExpenseToDelete(expense.id); setDeleteConfirmOpen(true); }}>
                                <Trash2 className="h-4 w-4 ml-1" />سڕینەوە
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => { onAddDialogOpenChange(open); if (!open) resetAddForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-right">زیادکردنی خەرجی نوێ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4" dir="rtl">
            <div className="space-y-2">
              <Label htmlFor="add-title">ناوی خەرجی *</Label>
              <Input
                id="add-title"
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                placeholder="بۆ نموونە: کرێی دوکان"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-amount">بڕ *</Label>
                <Input
                  id="add-amount"
                  type="number"
                  value={addAmount}
                  onChange={(e) => setAddAmount(e.target.value)}
                  placeholder="0"
                  dir="ltr"
                  className="text-left"
                />
              </div>
              <div className="space-y-2">
                <Label>دراو</Label>
                <Select value={addCurrency} onValueChange={(v: "IQD" | "USD") => setAddCurrency(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IQD">دینار (د.ع)</SelectItem>
                    <SelectItem value="USD">دۆلار ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>جۆر</Label>
                <Select value={addCategory} onValueChange={setAddCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-date">بەروار</Label>
                <Input
                  id="add-date"
                  type="date"
                  value={addDate}
                  onChange={(e) => setAddDate(e.target.value)}
                  dir="ltr"
                  className="text-left"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-desc">وەسف (ئارەزوومەندانە)</Label>
              <Input
                id="add-desc"
                value={addDescription}
                onChange={(e) => setAddDescription(e.target.value)}
                placeholder="وەسفی کورت..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-notes">تێبینی (ئارەزوومەندانە)</Label>
              <Textarea
                id="add-notes"
                value={addNotes}
                onChange={(e) => setAddNotes(e.target.value)}
                placeholder="تێبینی زیاتر..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex-row-reverse sm:flex-row-reverse gap-2">
            <Button onClick={handleAddExpense} disabled={!addTitle.trim() || !addAmount}>
              <Plus className="h-4 w-4 ml-2" />
              زیادکردن
            </Button>
            <Button variant="outline" onClick={() => { resetAddForm(); onAddDialogOpenChange(false); }}>
              پاشگەزبوونەوە
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setExpenseToEdit(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-right">دەستکاری خەرجی</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4" dir="rtl">
            <div className="space-y-2">
              <Label htmlFor="edit-title">ناوی خەرجی *</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="بۆ نموونە: کرێی دوکان"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-amount">بڕ *</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder="0"
                  dir="ltr"
                  className="text-left"
                />
              </div>
              <div className="space-y-2">
                <Label>دراو</Label>
                <Select value={editCurrency} onValueChange={(v: "IQD" | "USD") => setEditCurrency(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IQD">دینار (د.ع)</SelectItem>
                    <SelectItem value="USD">دۆلار ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>جۆر</Label>
                <Select value={editCategory} onValueChange={setEditCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-date">بەروار</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  dir="ltr"
                  className="text-left"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-desc">وەسف (ئارەزوومەندانە)</Label>
              <Input
                id="edit-desc"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="وەسفی کورت..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">تێبینی (ئارەزوومەندانە)</Label>
              <Textarea
                id="edit-notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="تێبینی زیاتر..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex-row-reverse sm:flex-row-reverse gap-2">
            <Button onClick={handleEditExpense} disabled={!editTitle.trim() || !editAmount}>
              <Edit className="h-4 w-4 ml-2" />
              نوێکردنەوە
            </Button>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              پاشگەزبوونەوە
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => { setDeleteConfirmOpen(open); if (!open) setExpenseToDelete(null); }}
        itemType="ئەم خەرجییە"
        onConfirm={async () => {
          if (expenseToDelete) {
            await onDeleteExpense(expenseToDelete);
            setExpenseToDelete(null);
          }
        }}
      />
    </div>
  );
}
